import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Sparkles, FileText, Check } from '../components/Icons.jsx';

export default function StrategyAudit() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    yearGroup: '',
    testDate: '',
    scores: { vr: 600, dm: 600, qr: 600, sjt: 2, mockTaken: false },
    targets: [],
    hours: '',
    weak: ''
  });

  const steps = [
    { title: 'Where are you in your application?', render: () => <YearGroup data={data} setData={setData} /> },
    { title: 'When are you sitting the UCAT?', render: () => <TestDate data={data} setData={setData} /> },
    { title: 'Your current mock scores (or estimate)', render: () => <Scores data={data} setData={setData} /> },
    { title: 'Target medical schools', render: () => <Targets data={data} setData={setData} /> },
    { title: 'Time available + weak spots', render: () => <HoursWeak data={data} setData={setData} /> }
  ];

  if (step >= steps.length) {
    return <AuditReport data={data} onRestart={() => { setStep(0); setData({ yearGroup: '', testDate: '', scores: { vr: 600, dm: 600, qr: 600, sjt: 2, mockTaken: false }, targets: [], hours: '', weak: '' }); }} />;
  }

  const progress = ((step + 1) / steps.length) * 100;

  return (
    <>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Free 30-min strategy audit · Step {step + 1} of {steps.length}</div>
        <h1>Build your personalised UCAT strategy</h1>
        <p className="subtitle">Five steps. Generates a 1-page strategy doc.</p>
      </div>

      <div className="progress" style={{ marginBottom: 22, height: 6 }}>
        <div className="progress-bar" style={{ width: `${progress}%` }} />
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <h2 style={{ margin: '0 0 18px', fontSize: 20 }}>{steps[step].title}</h2>
        {steps[step].render()}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 22 }}>
          {step > 0
            ? <button onClick={() => setStep(step - 1)} className="btn btn-secondary"><ArrowLeft width="16" height="16" />Back</button>
            : <span />}
          <button onClick={() => setStep(step + 1)} className="btn btn-primary">
            {step === steps.length - 1 ? 'Generate strategy' : 'Next'} <ArrowRight width="16" height="16" />
          </button>
        </div>
      </div>
    </>
  );
}

function YearGroup({ data, setData }) {
  const opts = ['Year 11', 'Year 12', 'Year 13', 'Gap year', 'Already applied — retaking'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {opts.map((o) => (
        <button key={o} onClick={() => setData({ ...data, yearGroup: o })} style={btnStyle(data.yearGroup === o)}>{o}</button>
      ))}
    </div>
  );
}

function TestDate({ data, setData }) {
  const opts = ['Within 4 weeks', '1–3 months', '3–6 months', '6+ months', 'Not booked yet'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {opts.map((o) => (
        <button key={o} onClick={() => setData({ ...data, testDate: o })} style={btnStyle(data.testDate === o)}>{o}</button>
      ))}
    </div>
  );
}

function Scores({ data, setData }) {
  const { vr, dm, qr, sjt, mockTaken } = data.scores;
  const update = (k, v) => setData({ ...data, scores: { ...data.scores, [k]: v, mockTaken: true } });
  return (
    <div>
      <label style={{ fontSize: 14, color: 'var(--text-muted)', display: 'block', marginBottom: 14 }}>
        <input type="checkbox" checked={!mockTaken} onChange={(e) => setData({ ...data, scores: { ...data.scores, mockTaken: !e.target.checked } })} style={{ marginRight: 8 }} />
        I haven't done a mock yet
      </label>
      {mockTaken && (
        <div className="r-grid-3" style={{ gap: 12 }}>
          {[['vr', 'VR'], ['dm', 'DM'], ['qr', 'QR']].map(([k, l]) => (
            <div key={k}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>{l}</label>
              <input type="number" min="300" max="900" step="10" value={data.scores[k]} onChange={(e) => update(k, Number(e.target.value))} style={inputStyle} />
            </div>
          ))}
        </div>
      )}
      {mockTaken && (
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>SJT band</label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            {[1, 2, 3, 4].map((b) => (
              <button key={b} onClick={() => update('sjt', b)} style={{ padding: '8px 14px', borderRadius: 999, border: `1px solid ${b === sjt ? 'var(--brand-purple)' : 'var(--border)'}`, background: b === sjt ? 'var(--accent-soft)' : '#fff', color: b === sjt ? 'var(--brand-deep)' : 'var(--text-2)', fontWeight: b === sjt ? 600 : 500 }}>Band {b}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Targets({ data, setData }) {
  const opts = ['Oxford / Cambridge', 'UCL / Imperial', "King's / Manchester / Edinburgh", 'Birmingham / Bristol / Newcastle', 'Other mid-tier (Sheffield, Leeds, Cardiff...)', 'Open-minded — anywhere with an offer'];
  const toggle = (o) => {
    const cur = data.targets || [];
    setData({ ...data, targets: cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o] });
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>Select all that apply.</p>
      {opts.map((o) => (
        <button key={o} onClick={() => toggle(o)} style={btnStyle((data.targets || []).includes(o))}>{o}</button>
      ))}
    </div>
  );
}

function HoursWeak({ data, setData }) {
  const hourOpts = ['1–3 hours/week', '4–7 hours/week', '8+ hours/week'];
  const weakOpts = ['Verbal Reasoning pace', 'Decision Making logic', 'Quantitative speed', 'SJT ranking', 'All sections about equal'];
  return (
    <>
      <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 8px' }}>Hours per week you can realistically commit:</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {hourOpts.map((o) => <button key={o} onClick={() => setData({ ...data, hours: o })} style={btnStyle(data.hours === o)}>{o}</button>)}
      </div>
      <p style={{ fontSize: 14, fontWeight: 600, margin: '20px 0 8px' }}>Weakest area:</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {weakOpts.map((o) => <button key={o} onClick={() => setData({ ...data, weak: o })} style={btnStyle(data.weak === o)}>{o}</button>)}
      </div>
    </>
  );
}

function AuditReport({ data, onRestart }) {
  const total = data.scores.mockTaken ? data.scores.vr + data.scores.dm + data.scores.qr : null;
  const gap = total ? Math.max(0, 2550 - total) : 350;

  const plan = useMemo(() => {
    const out = [];
    if (data.testDate === 'Within 4 weeks') {
      out.push({ title: 'Last-stretch strategy', body: 'No new content. Daily timed papers, alternating mocks and section drills. Sleep before pace.' });
    } else if (data.testDate === '1–3 months') {
      out.push({ title: 'Targeted prep window', body: 'Identify your two weakest section types. Spend 60% of time there. One full mock per fortnight, then weekly in the final month.' });
    } else {
      out.push({ title: 'Foundation phase', body: 'Two weeks of low-pressure exposure to all four sections. Take a diagnostic mock at the end of week 2 — then plan from real data.' });
    }
    if (data.weak === 'Verbal Reasoning pace') out.push({ title: 'VR pace fix', body: 'Run the Reading Lab "Learn how to chunk" drill 3x/week. Add Scan & Retrieve under timer. Target 350+ WPM by month end.' });
    if (data.weak === 'Decision Making logic') out.push({ title: 'DM logic fix', body: 'Syllogism + Venn drill daily. The "process-of-elimination" framework will recover 4–6 marks/paper.' });
    if (data.weak === 'Quantitative speed') out.push({ title: 'QR speed fix', body: 'Mental arithmetic drill daily (5 min). On-screen calculator practice — learn when NOT to use it. Numpad reflex.' });
    if (data.weak === 'SJT ranking') out.push({ title: 'SJT fix', body: 'Near-Miss Trainer + GMC framework drill. SJT can lift you a full band with structured prep — most students underweight it.' });
    if (gap >= 200) out.push({ title: 'High-gap warning', body: `You need ~${gap} points to reach 2550 (the typical UCL/KCL/Manchester threshold). That's reachable with structured prep, but unlikely without a tutor's pacing fixes.` });
    return out;
  }, [data, gap]);

  return (
    <>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Your strategy</div>
        <h1>Personalised UCAT strategy</h1>
        <p className="subtitle">A 1-page plan based on your inputs. Want it pressure-tested by a human? Book a free 15-minute call.</p>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>Your profile</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
          <Info label="Year group" value={data.yearGroup || '—'} />
          <Info label="Test date" value={data.testDate || '—'} />
          <Info label="Current total" value={total ? `${total} / 2700` : 'Not yet'} />
          <Info label="SJT band" value={data.scores.mockTaken ? `Band ${data.scores.sjt}` : '—'} />
          <Info label="Hours / week" value={data.hours || '—'} />
          <Info label="Weakest area" value={data.weak || '—'} />
        </div>
      </div>

      <h2 style={{ margin: '24px 0 14px', fontSize: 22 }}>Action plan</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {plan.map((p, i) => (
          <div key={i} className="card" style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--accent-soft)', color: 'var(--brand-purple)', display: 'grid', placeItems: 'center', flexShrink: 0, fontWeight: 700 }}>{i + 1}</div>
            <div>
              <h3 style={{ margin: 0, fontSize: 17 }}>{p.title}</h3>
              <p style={{ margin: '4px 0 0', color: 'var(--text-2)' }}>{p.body}</p>
            </div>
          </div>
        ))}
      </div>

      {(data.targets || []).length > 0 && (
        <div className="card" style={{ marginTop: 18 }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 18 }}>Your target schools</h3>
          <p style={{ margin: '0 0 12px', color: 'var(--text-2)' }}>You're aiming at:</p>
          <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--text-2)', lineHeight: 1.8 }}>
            {(data.targets || []).map((t) => <li key={t}>{t}</li>)}
          </ul>
          <p style={{ margin: '12px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>Use the <Link to="/where-can-i-apply" style={{ color: 'var(--accent)', fontWeight: 600 }}>Where Can I Apply</Link> tool to see realism against your scores.</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
        <button onClick={onRestart} className="btn btn-secondary">Re-run audit</button>
        <Link to="/tutor-match" className="btn btn-secondary">Find a tutor for this plan</Link>
        <a className="btn btn-primary">Have a human review this <ArrowRight width="16" height="16" /></a>
      </div>

      <section className="parents-cta" style={{ marginTop: 32 }}>
        <div>
          <h2>Want this strategy reviewed for free?</h2>
          <p>A Medic Mind advisor will pressure-test your plan in 15 minutes — honest read on whether the timeline is realistic and where you should actually focus.</p>
        </div>
        <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer">Book free call <ArrowRight width="16" height="16" /></a>
      </section>
    </>
  );
}

function Info({ label, value }) {
  return (
    <div style={{ padding: 14, background: 'var(--card-soft)', borderRadius: 10 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginTop: 4 }}>{value}</div>
    </div>
  );
}

const btnStyle = (active) => ({
  padding: '14px 18px', textAlign: 'left',
  border: `1px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`,
  background: active ? 'var(--accent-soft)' : '#fff',
  color: 'var(--text)', borderRadius: 12, fontSize: 15, fontWeight: 500, cursor: 'pointer'
});

const inputStyle = { width: '100%', padding: '10px 12px', fontSize: 16, fontWeight: 600, border: '1px solid var(--border-strong)', borderRadius: 10, marginTop: 6, background: '#fff' };
